"use strict";

// Copyright 2011-2022 The MathWorks, Inc.

/* global WebSocket */
define(['dojo/_base/connect', 'dojo/_base/declare', 'dojo/_base/Deferred', 'dojo/_base/lang', 'dojo/aspect', 'dojo/dom-construct', 'mw-log/Log', './CheckUrlService'], function (connect, declare, Deferred, lang, aspect, domConstruct, Log, CheckUrlService) {
  var IframeProxyClient = declare([], {
    /*
        As of 23b, the remote/proxy component and iframe handling has been removed.
        TODO: all naming conventions related to iframe must be updated
    */
    constructor: function constructor() {
      this._messageId = 0;
      this._loaded = false;
      this._pendingQueue = [];
      this._transactions = {};
      this._uploadTargetCallbacks = {};
      this._checkUrl = null;
      this._loadTimeoutHandle = null;
      this._checkUrlTimeout = null;
      this._errorRate = 0;
      this._inFlightTransactions = {};
      this._webSocket = null;
    },

    /**
    * Creates an new iframe for remote communication and sets the src URL
    * the iframe.  If this function was already called, it only creates a new
    * iframe if the existing iframe's src would have to change.
    *
    * @param {string} host              e.g. https://something.com/ -
    *                                       undefined and "" are normalized to "/"
    *                                       if host does not end in a trailing slash it is
    *                                       normalized to have a trailing slash
    * @param {string} key               Sets "routingkey" on url.  Used by the workerrouter
    *                                       to route to a worker.  Treats undefined and ""
    *                                       the same.
    * @param {string} computerToken     no longer used. Pass ""
    *                                       if refactoring - remove computeToken
    * @param {string} proxyUrl          a path that overrides the standard "remote/proxy" path
    *                                       if proxyUrl is set and not "", the iframe src will
    *                                       point to:  host+proxyUrl
    * @param {object} options           options object for passing additional parameters
    * @param {object} options.withCredentials if true, we always post credentials to the endpoint
    *                                        when using CORS / direct XHR requests (e.g not
    *                                        through an iFrame)
    *
    */
    setRemotePageHost: function setRemotePageHost(host, key, computeToken, proxyUrl) {
      var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
      host = this._combineUriPaths(host || '', '/');
      key = key || '';
      proxyUrl = proxyUrl || '';
      this.computeToken = computeToken; // withCredentials default is false, unless already set.

      this._withCredentials = this._withCredentials || false;

      if (!(typeof options.withCredentials === 'undefined' || options.withCredentials === null)) {
        // option object contains 'withCredentials' and is setting them to a value so use it.
        this._withCredentials = !!options.withCredentials;
      }

      if (host === this._host && key === this._key && proxyUrl === this._proxyUrl) {
        return;
      }

      this._errorLoading = false;
      this._host = host;
      this._key = key;
      this._proxyUrl = proxyUrl;

      this._cleanupFailureChecks();

      this._loaded = true;

      this._flushPending();
    },
    setLatency: function setLatency(latency) {
      this._latency = latency;
    },
    setErrorRate: function setErrorRate(errorRate) {
      this._errorRate = errorRate;
    },
    sendMessage: function sendMessage(message, endpoint, options) {
      var that = this;
      var id;
      var data;
      var transaction;

      if (this.computeToken) {
        options = options || {};
        options.headers = options.headers || {};
        options.headers.computeToken = this.computeToken;
      }

      var optionHeaders = options || {};
      id = this._getNewMessageId(); // Replace String

      data = {
        'action': 'sendMessage',
        'id': id,
        'endpoint': endpoint,
        'options': optionHeaders,
        'message': message
      };

      function canceler() {
        data = {
          action: 'abort',
          id: id
        };

        that._postMessage(data);
      }

      transaction = {
        deferred: new Deferred(canceler),
        id: id
      };

      if (this._errorLoading) {
        transaction.deferred.reject('Error loading: ' + this._url);
      } else {
        this._transactions[id] = transaction;

        this._postMessage(data);
      }

      return transaction.deferred;
    },
    _postMessage: function _postMessage(data) {
      if (this._loaded) {
        this._postMessageNow(data);
      } else {
        this._pendingQueue.push(data);
      }
    },
    _handleMessage: function _handleMessage(event) {
      var action, id, messageData;
      var closeEvent, msgEvent; // event.data {} format:
      // action: case
      // id: id
      // message: data
      // Handled actions:
      // ready
      // sendMessageResponse;<id>;<response type>;<response text>
      // uploadIframeMessage;<iframe id>;<data>

      action = event.data.action;

      if (!action) {
        throw new Error('Client unable to parse message, no action specified: ' + JSON.stringify(event.data));
      }

      switch (action) {
        case 'ready':
          this._cleanupFailureChecks();

          this._loaded = true;

          this._flushPending();

          break;

        case 'sendMessageResponse':
          this._sendMessageResponse(event);

          break;

        case 'uploadIframeMessage':
          id = event.data.id;
          messageData = event.data.message;

          this._uploadTargetCallbacks[id](messageData);

          break;

        case 'websocketOpened':
          this.onWebsocketOpen();
          break;

        case 'websocketClosed':
          closeEvent = event.data.message;
          this.onWebsocketClose(closeEvent);
          break;

        case 'websocketMsg':
          msgEvent = event.data.message;
          this.onWebsocketMsg(msgEvent);
          break;

        case 'websocketError':
          this.onWebsocketError();
          break;

        default:
          throw new Error('Unknown action: ' + action + ', data: ' + JSON.stringify(event.data));
      }
    },
    _sendMessageResponse: function _sendMessageResponse(event) {
      // NOTE: If changing this code, you may need to change:
      // _postMessageNow
      var transaction, message;
      var id = event.data.id;
      var responseType = event.data.responseType;
      var messageData = event.data.message;

      if (!id || !responseType) {
        throw new Error('Invalid message: ' + JSON.stringify(event.data));
      }

      transaction = this._transactions[id];

      if (!transaction) {
        throw new Error('Unknown transaction id: ' + id + ': ' + JSON.stringify(event.data));
      }

      delete this._transactions[id];

      if (!transaction.deferred.isCanceled()) {
        if (responseType === 'success') {
          try {
            // Convert from JSON if there is messageData success is
            // usually JSON but just in case, wrap it in a try/catch
            message = messageData && JSON.parse(messageData);
          } catch (e) {
            message = messageData;
          }

          message = message === '' ? {} : message;
          transaction.deferred.resolve(message);
          return;
        }

        this.onError(messageData); // Errors are just strings
        // HTTP Status Code if present is added to string object

        transaction.deferred.reject(messageData);
      }
    },
    _cleanupFailureChecks: function _cleanupFailureChecks() {
      if (this._checkUrlTimeout) {
        clearTimeout(this._checkUrlTimeout);
      }

      if (this._loadTimeoutHandle) {
        clearTimeout(this._loadTimeoutHandle);
      }

      if (this._checkUrl && !this._checkUrl.isFulfilled()) {
        this._checkUrl.cancel('Timeout Occurred', false);
      }

      this._checkUrlTimeout = null;
      this._loadTimeoutHandle = null;
      this._checkUrl = null;
    },
    _handleLoadTimeout: function _handleLoadTimeout() {
      var fault, transaction, transactions;

      if (this._loaded) {
        return;
      }

      fault = {
        code: 'IframeLoadTimeout',
        description: 'Iframe proxy loading failed due to an HTTP error',
        message: ''
      };
      transactions = this._transactions;
      this._transactions = {};
      this._pendingQueue = [];

      for (transaction in transactions) {
        if (transactions.hasOwnProperty(transaction)) {
          transactions[transaction].deferred.reject('Timeout: ' + this._url + ' at ' + new Date());
        }
      }

      this._errorLoading = true;
      this.onError(fault);
    },
    _handleXhrResponse: function _handleXhrResponse(id, response) {
      var data = {
        action: 'sendMessageResponse',
        id: id,
        responseType: 'success',
        message: response
      };

      this._handleMessage({
        data: data
      });
    },
    _handleXhrFault: function _handleXhrFault(id, response) {
      var data = {
        action: 'sendMessageResponse',
        id: id,
        responseType: 'fault',
        message: response
      };

      this._handleMessage({
        data: data
      });
    },
    _sendXHRNow: function _sendXHRNow(event) {
      var _this = this;

      var id, endpoint, messageData, options;
      var xhr, complete, header;
      var action = event.data.action;

      if (!action) {
        throw new Error('Unable to parse message, no action specified: ' + JSON.stringify(event.data));
      }

      switch (action) {
        case 'sendMessage':
          id = event.data.id;
          endpoint = event.data.endpoint;
          options = event.data.options;
          messageData = event.data.message;

          if (id && endpoint && messageData) {
            try {
              xhr = new XMLHttpRequest();
              this._inFlightTransactions[id] = {
                xhr: xhr
              };
              complete = false;

              xhr.onreadystatechange = function () {
                var status;

                if (xhr.readyState === 0) {
                  complete = true;

                  _this.handleXhrFault(id, 'XHR readyState 0');
                } else if (xhr.readyState === 4 && !complete) {
                  complete = true;
                  status = xhr.status || 0;

                  if (status >= 200 && status < 300 || status === 304) {
                    _this._handleXhrResponse(id, xhr.responseText);
                  } else {
                    _this._handleXhrFault(id, xhr.responseText);
                  }
                }

                if (complete && xhr) {
                  // memory leak prevention
                  xhr.onreadystatechange = _this._noop;
                  xhr = null;
                  delete _this._inFlightTransactions[id].xhr;
                  delete _this._inFlightTransactions[id];
                }
              };

              xhr.open('POST', endpoint, true);
              xhr.withCredentials = this._withCredentials;
              xhr.setRequestHeader('Content-Type', options.contentType ? options.contentType : 'application/json');
              xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

              if (options.headers) {
                for (header in options.headers) {
                  if (Object.prototype.hasOwnProperty.call(options.headers, header)) {
                    xhr.setRequestHeader(header, options.headers[header]);
                  }
                }
              }

              xhr.send(messageData);
            } catch (e) {
              this.handleXhrFault(id, 'Unable to send data: ' + e.toString());
            }
          } else {
            throw new Error('Invalid message to send: ' + JSON.stringify(event.data));
          }

          break;

        case 'abort':
          id = event.data.id;

          if (this._inFlightTransactions[id]) {
            this._inFlightTransactions[id].xhr.abort();
          }

          break;

        case 'connectWebsocket':
          var url = event.data.url;
          this._webSocket = new WebSocket(url);

          this._webSocket.onopen = function () {
            _this.onWebsocketOpen();
          };

          this._webSocket.onmessage = function (msg) {
            _this.onWebsocketMsg(msg);
          };

          this._webSocket.onclose = function (event) {
            var closeEvent = {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean
            };

            _this.onWebsocketClose(closeEvent);
          };

          this._webSocket.onerror = function () {
            _this.onWebsocketError();
          };

          break;

        case 'websocketSend':
          this._webSocket.send(event.data.data);

          break;

        case 'closeWebsocket':
          this._webSocket.close();

          break;

        default:
          throw new Error('Unknown action: ' + action);
      }
    },
    _postMessageNow: function _postMessageNow(data) {
      // NOTE: If changing this code, you may need to change:
      // _sendMessageResponse
      var that = this;
      var id, transaction;

      function doPost() {
        // generate an error response instead of do the real postMessage call
        if (that._errorRate > 0 && Math.random() < that._errorRate && data.action === 'sendMessage') {
          id = data.id;
          transaction = that._transactions[id];

          if (transaction) {
            delete that._transactions[id];
            Log.error('Generating simulated error. Request was: ', JSON.stringify(data));
            var error = '403 Forbidden;403';
            that.onError(error); // Errors are just strings
            // HTTP Status Code if present is added to string object

            transaction.deferred.reject(error);
          }
        } else {
          that._sendXHRNow({
            data: data
          });
        }
      }

      if (this._latency) {
        setTimeout(doPost, this._latency);
      } else {
        doPost();
      }
    },
    _flushPending: function _flushPending() {
      this._pendingQueue.forEach(this._postMessageNow, this);

      this._pendingQueue = [];
    },
    _getNewMessageId: function _getNewMessageId() {
      this._messageId += 1;
      return this._messageId.toString();
    },

    /**
     * onError event is fired when a xhr fault is received
     * Designed to aspect on
     * @params fault - a fault object
     * @returns {void}
     */
    onError: function onError()
    /* fault */
    {},
    onWebsocketOpen: function onWebsocketOpen() {},
    onWebsocketMsg: function onWebsocketMsg()
    /* msg */
    {},
    onWebsocketClose: function onWebsocketClose()
    /* event */
    {},
    onWebsocketError: function onWebsocketError() {},

    /**
     * Computes a valid Uri path from valid Uri segments
     * Usage:
     *   _combineUriPaths('/s1/s2/', '/s3/','s4','s5','', 'f1.html?q=1')
     * Will return:
     *  '/s1/s2/s3/s4/s5/f1.html?q=1'
     *
     * If a parameter is not valid Uri path on its own, the returned Uri is not
     * going to be valid.
     * So parameters like "x//y", "x y/", "d%d" would all result in a bad Uri.
     *
     * @param {...string} s - an argument list of path strings to combine
     * @returns {string} a combined uri from the arg list
     */
    _combineUriPaths: function _combineUriPaths()
    /* args */
    {
      var args = Array.prototype.slice.call(arguments);
      var combinedUri = args.reduce(function (a, b) {
        if (a.length === 0) {
          return b;
        } else if (b.length > 0) {
          if (b[0] === '/' && a.lastIndexOf('/') === a.length - 1) {
            b = b.substring(1);
          } else if (b[0] !== '/' && a.lastIndexOf('/') !== a.length - 1) {
            a += '/';
          }
        }

        return a + b;
      });
      return combinedUri;
    }
  });
  IframeProxyClient.WebSocket = declare([], {
    constructor: function constructor(url) {
      var that = this;
      this.proxyClient = IframeProxyClient.WebSocket.proxyClient;

      if (this.proxyClient.computeToken && url.indexOf('?') >= 0) {
        url = url + '&computeToken=' + this.proxyClient.computeToken;
      }

      var data = {
        action: 'connectWebsocket',
        url: url
      };

      this.proxyClient._postMessage(data);

      aspect.after(this.proxyClient, 'onWebsocketOpen', function () {
        that.onopen.apply(that, arguments);
      }, true);
      aspect.after(this.proxyClient, 'onWebsocketMsg', function () {
        that.onmessage.apply(that, arguments);
      }, true);
      aspect.after(this.proxyClient, 'onWebsocketClose', function () {
        that.onclose.apply(that, arguments);
      }, true);
      aspect.after(this.proxyClient, 'onWebsocketError', function () {
        // Notify MessageService NOT cometd RE: Errors
        // Let MessageService clean up and reset after a
        // MessageService Failure
        that.onerror.apply(that, arguments);
        that.proxyClient.onError.apply(that.proxyClient, arguments);
      }, true);
    },
    onopen: function onopen() {},
    onmessage: function onmessage()
    /* msg */
    {},
    onclose: function onclose()
    /* event */
    {},
    onerror: function onerror()
    /* event */
    {},
    send: function send(msg) {
      var data = {
        action: 'websocketSend',
        data: msg
      };

      this.proxyClient._postMessage(data);
    },
    close: function close() {
      var data = {
        action: 'closeWebsocket'
      };

      this.proxyClient._postMessage(data);
    }
  });
  return IframeProxyClient;
});
//# sourceMappingURL=IframeProxyClient.js.map
